"use client";

import { Markdown } from "@repo/ui/components/markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@repo/ui/components/ui/dialog";
import { Marker, MarkerContent } from "@repo/ui/components/ui/marker";
import {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceDescription,
  TraceIcon,
  TraceLine,
  TraceTitle,
} from "@repo/ui/components/ui/trace";
import { math } from "@streamdown/math";
import { BookOpenIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { ToolPart, Turn, TurnPart } from "../turn";
import { FhirQueryStep } from "./fhir-query-step";
import { StatusIcon, Step, StepCodeBlock, stepStatus } from "./trace-step";
import "katex/dist/katex.min.css";

type TextPart = Extract<TurnPart, { kind: "text" }>;
type ReasoningPart = Extract<TurnPart, { kind: "reasoning" }>;
type OpaquePart = Extract<TurnPart, { kind: "opaque" }>;
type CitationPart = Extract<TurnPart, { kind: "citation" }>;

/** The renderer registry the host app supplies. Each part `kind` gets exactly
 * one renderer, so a turn's rendering is one dispatch away from its data —
 * never a growing switch statement scattered across the package. `reasoning`
 * and `tool` render one Trace line each: `Turn` puts every step in one `Trace`. */
export interface PartRenderers {
  text: (p: { part: TextPart; turn: Turn }) => ReactNode;
  reasoning: (p: { part: ReasoningPart; turn: Turn }) => ReactNode;
  tool: (p: { part: ToolPart; turn: Turn }) => ReactNode;
  citation: (p: { parts: CitationPart[]; turn: Turn }) => ReactNode;
  /** The "Stopped" marker — rendered once per turn, not per part. */
  outcome: (p: { turn: Turn }) => ReactNode;
  opaque: (p: { part: OpaquePart; turn: Turn }) => ReactNode;
}

/**
 * Builds `PartRenderers["opaque"]` from a `renderAs -> renderer` map. The
 * package ships an opaque part's `data` byte-for-byte to whichever renderer
 * is registered for its `renderAs` and never interprets it — this is how a
 * host extension (e.g. a Recommendation card) reaches the UI without the
 * package learning what a pyramid is.
 *
 * An unregistered `renderAs` renders nothing and warns — never throws, never
 * blanks the rest of the turn. The warning fires on every render of that
 * part (no module-level dedupe state), so a part that re-renders warns
 * repeatedly; that's a deliberate simplicity trade, not an oversight.
 */
export function createOpaqueRenderer(
  registry: Record<string, (p: { part: OpaquePart; turn: Turn }) => ReactNode>,
): PartRenderers["opaque"] {
  return ({ part, turn }) => {
    const renderer = registry[part.renderAs];
    if (!renderer) {
      console.warn(`@repo/agent-chat: no renderer registered for opaque part "${part.renderAs}"`);
      return null;
    }
    return renderer({ part, turn });
  };
}

// Math stays on: clinical answers carry dosing formulas.
const MARKDOWN_PLUGINS = { math };

// ─── reasoning ───────────────────────────────────────────────────────────

/** Our reasoning is one concatenated part with no per-part `state`, so it is
 * running while the turn streams with no answer text yet and no tool in flight. */
function isReasoningRunning(turn: Turn): boolean {
  if (turn.outcome !== "streaming") return false;
  return !turn.parts.some((p) => (p.kind === "text" && p.text) || (p.kind === "tool" && p.state === "running"));
}

function ReasoningLine({ text, running }: { text: string; running: boolean }) {
  const [open, setOpen] = useState<boolean>();

  const line = (
    <TraceLine status={running ? "running" : undefined}>
      <TraceIcon>
        <StatusIcon status={running ? "running" : undefined} />
      </TraceIcon>
      <TraceTitle>{running ? "Thinking" : "Thought"}</TraceTitle>
    </TraceLine>
  );

  if (text.length === 0) {
    return line;
  }

  return (
    <TraceCollapsible open={open ?? running} onOpenChange={setOpen}>
      <TraceCollapsibleTrigger>{line}</TraceCollapsibleTrigger>
      <TraceCollapsibleContent>
        <Markdown plugins={MARKDOWN_PLUGINS}>{text}</Markdown>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

// ─── tool ────────────────────────────────────────────────────────────────

/** Per-tool verbs, as the Tool pattern's sheet example does ("Reading" / "Read"
 * / "Failed to read"). Anything unlisted reads "Running" / "Ran" / "Failed to
 * run" plus its humanized name. */
const TOOL_VERBS: Record<string, { running: string; done: string; failed: string; object: string }> = {
  rag_search: { running: "Searching", done: "Searched", failed: "Failed to search", object: "the knowledge base" },
  search_clinical_corpus: {
    running: "Searching",
    done: "Searched",
    failed: "Failed to search",
    object: "the knowledge base",
  },
};

const humanize = (name: string) => name.replace(/_/g, " ");

function toolTitle(name: string, phase: "running" | "done" | "failed"): string {
  const verbs = TOOL_VERBS[name];
  if (verbs) return `${verbs[phase]} ${verbs.object}`;
  const verb = phase === "running" ? "Running" : phase === "done" ? "Ran" : "Failed to run";
  return `${verb} ${humanize(name)}`;
}

/** One step of the Chain of Thought pattern. Our `ToolPart` has no approval
 * states: done, error, or still running. A tool with a step of its own renders
 * its output as rows, as the pattern's per-tool steps do; the rest show their
 * payload as code. */
function ToolLine({ part, streaming }: { part: ToolPart; streaming: boolean }) {
  if (part.name === "fhir_query") return <FhirQueryStep part={part} streaming={streaming} />;

  const status = stepStatus(part, streaming);
  const title = toolTitle(part.name, status === "running" ? "running" : status === "error" ? "failed" : "done");
  const output = part.state === "done" ? part.output : undefined;
  const hasDetail =
    status !== "running" && (part.state === "error" || part.input !== undefined || output !== undefined);

  return (
    <Step status={status} title={title}>
      {hasDetail && (
        <>
          {part.input !== undefined && <StepCodeBlock value={part.input} />}
          {part.state === "error" ? (
            <StepCodeBlock value={part.error} destructive />
          ) : (
            output !== undefined && <StepCodeBlock value={output} />
          )}
        </>
      )}
    </Step>
  );
}

// ─── sources ─────────────────────────────────────────────────────────────

/** The Sources pattern. Our corpus is internal Postgres chunks with no URL, so
 * each source is a button that opens its cited passage in a dialog instead of
 * a link out. Citations are distinct passages (same document, different
 * pages), so they are not deduped. */
function Sources({ sources }: { sources: CitationPart[] }) {
  const [selected, setSelected] = useState<CitationPart | null>(null);

  return (
    <>
      <Trace>
        <TraceCollapsible>
          <TraceCollapsibleTrigger>
            <TraceLine>
              <TraceIcon>
                <BookOpenIcon />
              </TraceIcon>
              <TraceTitle>
                Used {sources.length} {sources.length === 1 ? "source" : "sources"}
              </TraceTitle>
            </TraceLine>
          </TraceCollapsibleTrigger>
          <TraceCollapsibleContent>
            <Trace>
              {sources.map((source) => (
                <button key={source.id} type="button" onClick={() => setSelected(source)}>
                  <TraceLine>
                    <TraceTitle>{source.title}</TraceTitle>
                    {source.detail && <TraceDescription>{source.detail}</TraceDescription>}
                  </TraceLine>
                </button>
              ))}
            </Trace>
          </TraceCollapsibleContent>
        </TraceCollapsible>
      </Trace>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="!max-w-3xl max-h-[80vh] overflow-hidden">
          {selected && (
            <div className="overflow-hidden">
              <DialogHeader className="shrink-0">
                <DialogTitle className="truncate pr-6 text-base">{selected.title}</DialogTitle>
                <div className="flex items-center gap-3 text-muted-foreground text-xs">
                  {selected.detail && <span>{selected.detail}</span>}
                  {selected.score !== undefined && <span>Score: {Math.round(selected.score * 100)}%</span>}
                </div>
              </DialogHeader>
              <div
                className="mt-4 max-h-[60vh] overflow-y-auto text-sm leading-relaxed"
                style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
              >
                {selected.excerpt ? (
                  <Markdown plugins={MARKDOWN_PLUGINS}>{selected.excerpt}</Markdown>
                ) : (
                  <p className="text-muted-foreground">No passage text available for this source.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * MIN-293 — the house rendering: one dispatch per part `kind`, so `Turn`
 * wraps rather than replaces them. Every unnamed kind in an app's
 * `renderers` override falls back to this (see `mergePartRenderers`).
 */
export const houseRenderers: PartRenderers = {
  text: ({ part }) => <Markdown plugins={MARKDOWN_PLUGINS}>{part.text}</Markdown>,
  reasoning: ({ part, turn }) => <ReasoningLine text={part.text} running={isReasoningRunning(turn)} />,
  tool: ({ part, turn }) => <ToolLine part={part} streaming={turn.outcome === "streaming"} />,
  citation: ({ parts }) => <Sources sources={parts} />,
  outcome: ({ turn }) =>
    turn.outcome === "interrupted" ? (
      <Marker>
        <MarkerContent>Stopped</MarkerContent>
      </Marker>
    ) : null,
  opaque: createOpaqueRenderer({}),
};

/**
 * Merges a partial `renderers` override (the issue's "prop surface — pinned"
 * `Root.renderers`) over the house defaults — a partial object overrides only
 * the kinds it names; every unnamed kind keeps `houseRenderers`' rendering,
 * never a blank turn.
 */
export function mergePartRenderers(overrides?: Partial<PartRenderers>): PartRenderers {
  return { ...houseRenderers, ...overrides };
}
